import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ActivitiesManager } from '@/components/activities/ActivitiesManager';
import { eventService } from '@/lib/api/event.service';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import type { Event } from '@/types/event.types';

export const ActivityManagement: React.FC = () => {
  const { t } = useTranslation(['activities', 'common']);
  const { eventId } = useParams();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canManageActivities, setCanManageActivities] = useState(false);

  const eventIdNum = parseInt(eventId || '0');

  useEffect(() => {
    if (eventIdNum) {
      loadEvent();
    }
  }, [eventIdNum]);

  const loadEvent = async () => {
    setLoading(true);
    setError(null);

    try {
      const eventData = await eventService.getEvent(eventIdNum);
      setEvent(eventData);
      
      // Check if user can manage activities
      // This would typically come from the event data or user permissions
      // For now, we'll assume organizers can manage activities
      setCanManageActivities(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load event');
      console.error('Error loading event:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading event...</span>
      </div>
    );
  }

  if (error || !event) {
    return (
      <PageContainer width="wide" className="space-y-6">
        <AppBreadcrumbs />
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error || 'Event not found'}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs />
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('activities:title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          For event: <span className="font-medium">{event.title}</span>
        </p>
      </div>

      {/* Activities Manager */}
      <ActivitiesManager eventId={eventIdNum} canManage={canManageActivities} />
    </PageContainer>
  );
};
